// The colors words can be in, and highlighted with: a few, as Jira's palette has, so a draft's
// colors stay readable and mean something. Each is written as the color it is on a white page,
// where what's copied is pasted, and any color read from elsewhere, as from a page pasted in or a
// version written, is taken as the nearest of them.

export type TextColor = 'gray' | 'red' | 'orange' | 'green' | 'blue' | 'purple';
export type Highlight = 'yellow' | 'green' | 'blue' | 'red' | 'purple';

export const TEXT_COLORS: Readonly<Record<TextColor, string>> = {
  gray: '#6b7280',
  red: '#dc2626',
  orange: '#ea580c',
  green: '#16a34a',
  blue: '#2563eb',
  purple: '#9333ea',
};

/** Yellow is what <mark> means without a color of its own. */
export const HIGHLIGHTS: Readonly<Record<Highlight, string>> = {
  yellow: '#fef08a',
  green: '#bbf7d0',
  blue: '#bfdbfe',
  red: '#fecaca',
  purple: '#e9d5ff',
};

export const TEXT_COLOR_NAMES = Object.keys(TEXT_COLORS) as TextColor[];
export const HIGHLIGHT_NAMES = Object.keys(HIGHLIGHTS) as Highlight[];

type Rgb = readonly [number, number, number];

// The names a color is most often written with.
const NAMED: Readonly<Record<string, string>> = {
  black: '#000000',
  white: '#ffffff',
  gray: '#808080',
  grey: '#808080',
  silver: '#c0c0c0',
  red: '#ff0000',
  maroon: '#800000',
  orange: '#ffa500',
  yellow: '#ffff00',
  lime: '#00ff00',
  green: '#008000',
  teal: '#008080',
  cyan: '#00ffff',
  aqua: '#00ffff',
  blue: '#0000ff',
  navy: '#000080',
  purple: '#800080',
  violet: '#ee82ee',
  magenta: '#ff00ff',
  fuchsia: '#ff00ff',
  pink: '#ffc0cb',
  brown: '#a52a2a',
};

/** A CSS color, such as #c00, rgb(204, 0, 0), or red, as red, green, and blue; null otherwise. */
export function readColor(value: string): Rgb | null {
  const written = value.trim().toLowerCase();
  const color = NAMED[written] ?? written;
  const hex = /^#([\da-f]{3}|[\da-f]{6})$/u.exec(color)?.[1];
  if (hex) {
    const full = hex.length === 3 ? hex.replace(/./gu, '$&$&') : hex;
    return [0, 2, 4].map((at) => Number.parseInt(full.slice(at, at + 2), 16)) as unknown as Rgb;
  }
  const rgb =
    /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)\s*(?:[,/]\s*([\d.]+)(%?)\s*)?\)$/u.exec(color);
  if (!rgb) return null;
  // Altogether see-through, as Docs writes the background of words it doesn't highlight.
  const alpha = rgb[4] === undefined ? 1 : Number(rgb[4]) / (rgb[5] ? 100 : 1);
  if (alpha === 0) return null;
  return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
}

/** Its hue, from 0 to 360; how colorful it is, from 0 to 1; and how light, from 0 to 1. */
function hsl([r, g, b]: Rgb): [number, number, number] {
  const max = Math.max(r, g, b) / 255;
  const min = Math.min(r, g, b) / 255;
  const light = (max + min) / 2;
  const chroma = max - min;
  if (chroma === 0) return [0, 0, light];
  const [red, green, blue] = [r / 255, g / 255, b / 255];
  const hue =
    max === red
      ? ((green - blue) / chroma + 6) % 6
      : max === green
        ? (blue - red) / chroma + 2
        : (red - green) / chroma + 4;
  return [hue * 60, chroma / (1 - Math.abs(2 * light - 1) || 1), light];
}

const hueOf = (hex: string) => hsl(readColor(hex) ?? [0, 0, 0])[0];
const hueGap = (a: number, b: number) => Math.min(Math.abs(a - b), 360 - Math.abs(a - b));

function nearest<Name extends string>(
  rgb: Rgb,
  palette: Readonly<Record<Name, string>>,
  names: readonly Name[],
): Name {
  const [hue] = hsl(rgb);
  const gap = (name: Name) => hueGap(hue, hueOf(palette[name]));
  return names.reduce((best, name) => (gap(name) < gap(best) ? name : best));
}

/** Whether a color is a color at all, rather than black, white, or a grey between. */
export function isColorful(rgb: Rgb): boolean {
  return Math.max(...rgb) - Math.min(...rgb) >= 48;
}

/** The text color nearest to `value`: grey for black, white, or a grey. */
export function textColorOf(value: string): TextColor | null {
  const rgb = readColor(value);
  if (!rgb) return null;
  if (!isColorful(rgb)) return 'gray';
  return nearest(
    rgb,
    TEXT_COLORS,
    TEXT_COLOR_NAMES.filter((name) => name !== 'gray'),
  );
}

/** The highlight nearest to `value`: yellow, as <mark> is, for one that isn't a color. */
export function highlightOf(value: string): Highlight | null {
  const rgb = readColor(value);
  if (!rgb) return null;
  return isColorful(rgb) ? nearest(rgb, HIGHLIGHTS, HIGHLIGHT_NAMES) : 'yellow';
}

/**
 * A color pasted on words, as the color they're in, or null where it's how all of a page reads,
 * such as black or grey, or too dark or too light to mean anything on its own.
 */
export function pastedTextColor(value: string): TextColor | null {
  const rgb = readColor(value);
  if (!rgb || !isColorful(rgb)) return null;
  const [, , light] = hsl(rgb);
  return light < 0.18 || light > 0.9 ? null : textColorOf(value);
}

/** A background pasted behind words, as their highlight, or null where it isn't one. */
export function pastedHighlight(value: string): Highlight | null {
  const rgb = readColor(value);
  if (!rgb || !isColorful(rgb)) return null;
  const [, , light] = hsl(rgb);
  // A dark background is a dark page's, or a code block's, not a highlight.
  return light < 0.35 ? null : highlightOf(value);
}
